/**
 * Feste Bereiche (BE-02) - rein und getestet.
 *
 * Drei Bereiche gibt es immer: Gesundheit & Körper, Schule, Finanzen. Sie
 * lassen sich umbenennen, umfärben, umsortieren und archivieren, aber nicht
 * löschen. `planFeste` sagt, was fehlt: markieren (und umbenennen, solange der
 * Bereich noch seinen alten Standardnamen trägt), anlegen, und - bei doppelt
 * markierten Bereichen - nichts, denn zusammenführen darf nur Ole.
 *
 * Neu angelegte feste Bereiche bekommen eine feste ID. Legen zwei Geräte sie
 * gleichzeitig an, ist es mit Sync dieselbe Zeile und nicht doppelt.
 */
import { AREA_COLORS } from '@/domain/areaColors';
import type { Area, FesterBereich } from '@/domain/types';

/** Die Farben, mit denen der Erststart die beiden alten Bereiche angelegt hat. */
const ALTE_STANDARDFARBEN: readonly string[] = [AREA_COLORS[0]!.value, AREA_COLORS[1]!.value];

export interface FesteVorgabe {
  fest: FesterBereich;
  id: string;
  name: string;
  icon: string;
  /** Schlüssel in `BEREICHS_VORSCHLAEGE` (D-09). */
  farbe: string;
  /** Frühere Standardnamen, auf die ein vorhandener Bereich passt (kleingeschrieben). */
  alteNamen: readonly string[];
}

export const FESTE_BEREICHE: readonly FesteVorgabe[] = [
  { fest: 'gesundheit', id: 'fest-gesundheit', name: 'Gesundheit & Körper', icon: 'heart-pulse', farbe: 'v-gruen', alteNamen: ['gesundheit', 'gesundheit & körper', 'gesundheit und körper', 'körper'] },
  { fest: 'schule', id: 'fest-schule', name: 'Schule', icon: 'graduation-cap', farbe: 'v-blau', alteNamen: ['schule', 'schule/studium', 'studium', 'schule & studium'] },
  { fest: 'finanzen', id: 'fest-finanzen', name: 'Finanzen', icon: 'wallet', farbe: 'v-gold', alteNamen: ['finanzen', 'geld'] },
] as const;

export type FesteAktion =
  | { art: 'markieren'; id: string; fest: FesterBereich; name?: string; farbe?: string }
  | { art: 'anlegen'; vorgabe: FesteVorgabe };

const schluessel = (name: string) => name.trim().toLowerCase();

/** Was zu tun ist, damit alle drei festen Bereiche da und markiert sind. */
export function planFeste(areas: readonly Area[]): FesteAktion[] {
  const aktionen: FesteAktion[] = [];
  for (const v of FESTE_BEREICHE) {
    if (areas.some((a) => a.fest === v.fest)) continue;
    // Ein Bereich mit dem alten Standardnamen wird zum festen Bereich: Name und
    // Verknüpfungen bleiben, er wird nur markiert (und neu benannt).
    const passend = areas.filter((a) => !a.fest && v.alteNamen.includes(schluessel(a.name)))
      .sort((a, b) => Number(a.archived) - Number(b.archived) || a.createdAt.localeCompare(b.createdAt))[0];
    if (passend) {
      aktionen.push({
        art: 'markieren', id: passend.id, fest: v.fest,
        ...(schluessel(passend.name) !== schluessel(v.name) ? { name: v.name } : {}),
        // Nur die alte Standardfarbe wird durch die neue ersetzt; eine selbst gewaehlte bleibt.
        ...(ALTE_STANDARDFARBEN.includes(passend.color) ? { farbe: v.farbe } : {}),
      });
    } else {
      aktionen.push({ art: 'anlegen', vorgabe: v });
    }
  }
  return aktionen;
}
